/**
 * Onboarding Guard
 *
 * CANONICAL FLOW:
 * 1. Signup → /onboarding/welcome (step 1 of 4)
 * 2. Complete onboarding steps 1-4
 * 3. Write onboardingCompletedAt to team doc
 * 4. Navigate to dashboard (or chosen first action)
 * 5. Never see onboarding again
 *
 * Redirects users based on their onboarding state:
 * - No team → /onboarding/welcome (handles team creation inline)
 * - Has team but no onboarding completed → /onboarding/welcome (fullscreen onboarding)
 * - Has team + onboarding completed → children (main app)
 *
 * IMPORTANT: This guard does NOT run on /onboarding/ routes to prevent loops.
 * The WelcomeOnboardingPage handles its own navigation after completion.
 */

import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTeam } from '@/contexts/TeamContext';
import { useGlobalAdminContext } from '@/contexts/GlobalAdminContext';
import { usePlatformAdminRedirect } from '@/components/guards/PlatformAdminRedirect';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { logger } from '@/lib/logger';
import { getPrimaryProvider } from '@/lib/auth/providerUtils';
import { AccessDisabledScreen } from '@/components/screens/AccessDisabledScreen';
import { TeamDeactivatedScreen } from '@/components/screens/TeamDeactivatedScreen';
import { useTeamOwnerEmail } from '@/hooks/useTeamOwnerEmail';
import { useSignupFlowDecision } from '@/lib/routing/useSignupFlowDecision';

interface OnboardingGuardProps {
  children: ReactNode;
}

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { t } = useTranslation('common');
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const {
    activeTeamId,
    team,
    loading: teamLoading,
    error: teamError,
    onboardingJustCompleted,
    isOnboardingInProgress,
    isMemberDisabled,
    isDeactivated,
    membershipLoadStatus,
    bootErrorCode,
    retryBoot,
  } = useTeam();

  /**
   * ✅ FIX: Hook order must NEVER change between renders.
   * In the previous version, `usePlatformAdminRedirect()` and `useGlobalAdminContext()`
   * were called AFTER early returns (onboarding routes, loading, membership errors, etc.).
   *
   * That means on some renders they were not called at all, and on later renders they were,
   * producing: "Rendered more hooks than during the previous render."
   *
   * Solution:
   * - Call hooks unconditionally at the top
   * - Gate behavior inside the hook via `enabled` flag (or no-op when disabled)
   *
   * NOTE: This assumes `usePlatformAdminRedirect` supports an options param.
   * If it currently does not, update it to accept `{ enabled?: boolean }`
   * and to early-return inside its internal `useEffect`.
   */
  const { isPlatformAdmin, isLoading: adminLoading } = useGlobalAdminContext();

  // Fetch owner email only when member is disabled (for AccessDisabledScreen contact button)
  // Hook is called unconditionally to preserve hook order; enabled flag gates the Firestore read
  const ownerEmail = useTeamOwnerEmail(activeTeamId, !authLoading && isMemberDisabled);

  // P1 COMPLETION: Use resolver as single source of truth for routing decisions
  const decision = useSignupFlowDecision();

  // Check onboarding route first (but AFTER hooks to keep hook order stable)
  const isOnboardingRoute = location.pathname.startsWith('/onboarding');

  // Platform admin redirect should only run on NON-onboarding routes,
  // and only after admin status is resolved.
  usePlatformAdminRedirect({
    enabled: !isOnboardingRoute && !adminLoading,
  });

  // If we're on any onboarding route via the /* catch-all, show loading
  // The specific onboarding routes should handle these paths, not the catch-all
  // This prevents the guard from showing wrong content during route transitions
  if (isOnboardingRoute) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">{t('status.loading')}</p>
        </div>
      </div>
    );
  }

  // CONTRACT DETECTION ORDER (Team Deactivation & Access Gating Contract V1.0 §3):
  // 1. Check team-level deactivation (isDeactivated) FIRST
  // 2. Then check member-level access (isMemberDisabled)
  // Both checks run after auth loading resolves but before teamLoading check,
  // so blocked users never see a flash of the main app.

  // 1. Team deactivated — show TeamDeactivatedScreen (all members see this)
  if (!authLoading && isDeactivated) {
    return <TeamDeactivatedScreen teamName={team?.name} />;
  }

  // 2. Member-level access disabled (active=false) — show AccessDisabledScreen
  // The member can still read their own membership doc (Firestore rule exception).
  if (!authLoading && isMemberDisabled) {
    return <AccessDisabledScreen teamName={team?.name} ownerEmail={ownerEmail} />;
  }

  // Show loading while checking auth and team (for non-onboarding routes)
  // Boot stability: Show different message for 'slow' state
  // OAuth bootstrap: Wait for oauthBootstrapComplete before evaluating resolver
  if (authLoading || teamLoading || isOnboardingInProgress) {
    const isSlowLoading = membershipLoadStatus === 'slow';
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4 max-w-md px-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">
            {isSlowLoading ? 'Dit duurt langer dan normaal…' : t('status.loading')}
          </p>
          {isSlowLoading && (
            <p className="text-xs text-muted-foreground">Even geduld, we laden je gegevens.</p>
          )}
        </div>
      </div>
    );
  }

  // Not authenticated - let ProtectedRoute handle this
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Handle membership query errors - show error state, NOT onboarding redirect
  // These errors mean we couldn't determine if user has memberships, so we can't redirect to create-team
  const membershipErrorTypes = [
    'MEMBERSHIP_PERMISSION_DENIED',
    'MEMBERSHIP_QUERY_FAILED',
    'MEMBERSHIP_QUERY_TIMEOUT',
  ];
  if (!activeTeamId && teamError && membershipErrorTypes.includes(teamError)) {
    // FIX A3: If onboarding is in progress, show friendly "setup in progress" message
    // instead of scary error state. This handles the brief window where team creation
    // just completed but membership query hasn't caught up yet.
    if (isOnboardingInProgress) {
      logger.info('[OnboardingGuard] Membership error during onboarding, showing setup message', {
        teamError,
      });
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center space-y-4 max-w-md px-4">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <h2 className="text-lg font-semibold">
              {t('onboarding.settingUp', 'We ronden je setup af…')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('onboarding.settingUpDescription', 'Even geduld, je bedrijf wordt aangemaakt.')}
            </p>
          </div>
        </div>
      );
    }

    // Not in onboarding - show actual error state
    logger.error('[OnboardingGuard] Membership query error, showing error state', {
      teamError,
      bootErrorCode,
    });

    // Boot stability: Show retry button for MEMBERSHIP_TIMEOUT
    const isTimeout = bootErrorCode === 'MEMBERSHIP_TIMEOUT';

    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4 max-w-md px-4">
          <div className="text-destructive text-4xl">⚠️</div>
          <h2 className="text-lg font-semibold">
            {isTimeout
              ? 'Verbinding duurt te lang'
              : t('errors.teamLoadFailed', 'Could not load team data')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isTimeout
              ? 'Het laden van je gegevens duurt langer dan verwacht. Controleer je internetverbinding en probeer het opnieuw.'
              : teamError === 'MEMBERSHIP_PERMISSION_DENIED'
                ? t(
                    'errors.permissionDenied',
                    'Permission denied. Please try logging out and back in.'
                  )
                : t('errors.tryAgainLater', 'Please try again later or contact support.')}
          </p>
          <Button
            onClick={isTimeout ? retryBoot : () => window.location.reload()}
            variant="default"
            className="mt-4"
          >
            {isTimeout ? 'Opnieuw proberen' : t('actions.refresh', 'Refresh')}
          </Button>
        </div>
      </div>
    );
  }

  // Wait for admin status to resolve before making NO_TEAM decisions
  // (Hook already ran; this is just UI gating.)
  if (adminLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">{t('status.loading')}</p>
        </div>
      </div>
    );
  }

  // Platform admins without teams: allow them to pass through
  // The PlatformAdminRedirect hook will redirect them to /admin/teams if needed
  // If they DO have a team, they can use the normal app like any other user
  if (isPlatformAdmin && teamError === 'NO_TEAM') {
    // Let the children render - PlatformAdminRedirect will handle routing
    return <>{children}</>;
  }

  // If resolver says we should be on a different route, redirect there
  // This handles ALL cases: no team, incomplete onboarding, team picker, etc.
  // decision.to can be null to explicitly allow the current route
  if (decision.to !== null && decision.to !== location.pathname) {
    const provider = user ? getPrimaryProvider(user) : 'unknown';
    logger.info('[OnboardingGuard] Resolver routing decision', {
      from: location.pathname,
      to: decision.to,
      reason: decision.reason,
      provider,
    });
    return <Navigate to={decision.to} replace={decision.replace} />;
  }

  // Resolver says current route is correct (to === null or to === current path) - allow access
  return <>{children}</>;
}
